import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Button, Div, Text } from "atomize"
import { AnimatePresence, MotionConfig, animate, motion, useReducedMotion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faBook,
	faDumbbell,
	faList,
	faPlus,
	faTableCellsLarge,
	faUser,
	faUserGroup,
} from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import EmptyState from "../atoms/EmptyState"
import TextInput from "../inputs/TextInput"
import { isSmartphone } from "../atoms/SmartphoneView"
import { ABOVE_PHONE_NAV } from "../atoms/phoneBars"
import PhonePageTitle, { usePhoneSummary } from "../header/PhonePageTitle"
import { modalContext } from "../providers/modal.provider"
import { notebookContext } from "../providers/notebook.provider"
import NotebookRow from "./NotebookRow"
import NotebookGallery, { SWAP } from "./NotebookGallery"
import NotebookPhaseList from "./NotebookPhaseList"
import NotebookExerciseView from "./NotebookExerciseView"
import { LevelMark, levelPill } from "./NotebookVariants"
import {
	NOTEBOOK_ALL,
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LESSON,
	NOTEBOOK_OWNERS,
	filterByOwner,
	filterNotebook,
	notebookFilters,
	notebookLevelFilters,
	searchNotebook,
} from "../utils/notebook.utils"

const TABS = [
	{ kind: NOTEBOOK_LESSON, label: "Lezioni" },
	{ kind: NOTEBOOK_EXERCISE, label: "Esercizi" },
]

// "3 lezioni · 5 esercizi" nella pillola in cima al telefono; col quaderno
// vuoto niente
export const notebookSummary = counts => {
	const lessons = counts[NOTEBOOK_LESSON] ?? 0
	const exercises = counts[NOTEBOOK_EXERCISE] ?? 0
	if (!lessons && !exercises) return null
	return [
		lessons === 1 ? "1 lezione" : `${lessons} lezioni`,
		exercises === 1 ? "1 esercizio" : `${exercises} esercizi`,
	].join(" · ")
}

const EMPTY = {
	[NOTEBOOK_LESSON]: {
		icon: faBook,
		title: "Ancora nessuna lezione",
		text: "Le lezioni da rifare: obiettivo, su cosa si lavora e le quattro fasi coi loro esercizi. Si salvano dal programma di una lezione, con «Salva nel quaderno», oppure da qui.",
		actionText: "Scrivi una lezione",
	},
	[NOTEBOOK_EXERCISE]: {
		icon: faDumbbell,
		title: "Ancora nessun esercizio",
		text: "Un esercizio solo, da riprendere in qualsiasi lezione: di una fase precisa o buono per tutte. Si salva dal segnalibro accanto a un esercizio, oppure da qui.",
		actionText: "Scrivi un esercizio",
	},
}

// Le linguette, come quelle di un raccoglitore: quella scelta è dello stesso
// bianco del foglio e ci si attacca sopra, l'altra resta indietro, più
// bassa e del colore caldo. Accanto, quante voci ci sono dietro.
function NotebookTabs({ kind, counts, onChange }) {
	return (
		<Div
			d="flex"
			align="flex-end"
			role="tablist"
			aria-label="Lezioni o esercizi"
			style={{ gap: "0.375rem" }}>
			{TABS.map(tab => {
				const isSelected = kind === tab.kind
				return (
					<button
						key={tab.kind}
						type="button"
						role="tab"
						aria-selected={isSelected}
						onClick={() => onChange(tab.kind)}
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
							height: isSelected ? "2.875rem" : "2.5rem",
							padding: "0 1.25rem",
							border: 0,
							borderRadius: "18px 18px 0 0",
							fontFamily: "inherit",
							fontSize: "0.9375rem",
							fontWeight: 600,
							cursor: "pointer",
							color: isSelected ? theme.colors.gunmetal : theme.colors.text_light,
							background: isSelected ? theme.colors.surface : theme.colors.primary_light,
							transition: "height 0.15s, background 0.15s, color 0.15s",
						}}>
						{tab.label}
						{/* il numero si vede, ma il nome della linguetta resta il suo */}
						<span
							aria-hidden="true"
							style={{ fontSize: "0.75rem", color: theme.colors.text_light }}>
							{counts[tab.kind]}
						</span>
					</button>
				)
			})}
		</Div>
	)
}

// Gli esercizi in galleria o a lista: la scelta resta su questo telefono.
// Chi non l'ha mai fatta li trova in galleria se almeno uno ha lo schema, a
// lista se no: una griglia di campi sfocati non dice niente a nessuno.
const EXERCISES_VIEWS = [
	{ id: "grid", label: "Griglia", icon: faTableCellsLarge },
	{ id: "list", label: "Lista", icon: faList },
]

const EXERCISES_VIEW_STORAGE_KEY = "incampo.notebookExercisesView"

const readExercisesView = () => {
	try {
		const stored = window.localStorage.getItem(EXERCISES_VIEW_STORAGE_KEY)
		return EXERCISES_VIEWS.some(view => view.id === stored) ? stored : null
	} catch {
		return null
	}
}

const storeExercisesView = view => {
	try {
		window.localStorage.setItem(EXERCISES_VIEW_STORAGE_KEY, view)
	} catch {
		// vale finché non si chiude l'app
	}
}

// L'interruttore, accanto alle linguette: due icone in una pastiglia, quella
// scelta piena.
function ExercisesViewToggle({ view, onChange }) {
	return (
		<div
			role="group"
			aria-label="Come vedere gli esercizi"
			style={{
				display: "inline-flex",
				gap: "2px",
				padding: "3px",
				marginBottom: "0.375rem",
				borderRadius: "14px",
				background: theme.colors.surface,
			}}>
			{EXERCISES_VIEWS.map(option => {
				const isSelected = option.id === view
				return (
					<button
						key={option.id}
						type="button"
						aria-label={option.label}
						aria-pressed={isSelected}
						onClick={() => onChange(option.id)}
						style={{
							width: "2.125rem",
							height: "1.875rem",
							border: 0,
							borderRadius: "11px",
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							color: isSelected ? theme.colors.white : theme.colors.text_light,
							background: isSelected ? theme.colors.gunmetal_fill : "transparent",
							transition: "background 0.15s, color 0.15s",
						}}>
						<FontAwesomeIcon icon={option.icon} />
					</button>
				)
			})}
		</div>
	)
}

// Le pastiglie che restringono le voci: "Tutte", i livelli delle varianti
// e, dopo un filo, su cosa si lavora per le lezioni e la fase per gli
// esercizi. Un livello e una pastiglia si sommano; toccare quello scelto lo
// toglie, e "Tutte" li toglie tutt'e due. Scorrono di lato se non ci stanno.
function NotebookFilters({ filters, selected, onChange, levels, level, onLevel }) {
	const [all, ...options] = filters
	const isAll = selected === NOTEBOOK_ALL && !level
	return (
		<div
			role="group"
			aria-label={filters[0]?.label === "Tutti" ? "Fase" : "Su cosa si lavora"}
			style={{
				display: "flex",
				alignItems: "center",
				gap: "0.5rem",
				padding: "0.875rem 1rem 0.75rem",
				overflowX: "auto",
				scrollbarWidth: "none",
			}}>
			<button
				type="button"
				aria-pressed={isAll}
				onClick={() => {
					onChange(NOTEBOOK_ALL)
					onLevel(null)
				}}
				style={levelPill(isAll, theme.colors.background)}>
				{all.label}
			</button>
			{levels.map(option => {
				const isSelected = option.id === level
				return (
					<button
						key={option.id}
						type="button"
						aria-pressed={isSelected}
						onClick={() => onLevel(isSelected ? null : option.id)}
						style={levelPill(isSelected, theme.colors.background)}>
						<LevelMark levelID={option.id} />
					</button>
				)
			})}
			{levels.length > 0 && options.length > 0 && (
				<span
					aria-hidden="true"
					style={{
						flex: "none",
						width: "1.5px",
						height: "1.125rem",
						borderRadius: "1px",
						background: theme.colors.gunmetal_lighter,
					}}
				/>
			)}
			{options.map(filter => {
				const isSelected = filter.id === selected
				return (
					<button
						key={filter.id}
						type="button"
						aria-pressed={isSelected}
						onClick={() => onChange(isSelected ? NOTEBOOK_ALL : filter.id)}
						style={levelPill(isSelected, theme.colors.background)}>
						{filter.label}
					</button>
				)
			})}
		</div>
	)
}

// Di chi sono le voci da vedere: tutte, solo le proprie o solo quelle dei
// colleghi. Il selettore sta nella barra in basso, a sinistra della ricerca:
// sopra il foglio avrebbe tolto posto alle voci. La scelta resta sul
// telefono.
const OWNER_ICONS = { all: faBook, mine: faUser, colleagues: faUserGroup }
const OWNER_STORAGE_KEY = "incampo.notebookOwner"

const readOwner = () => {
	try {
		const stored = window.localStorage.getItem(OWNER_STORAGE_KEY)
		return NOTEBOOK_OWNERS.some(owner => owner.id === stored) ? stored : "all"
	} catch {
		return "all"
	}
}

const storeOwner = owner => {
	try {
		window.localStorage.setItem(OWNER_STORAGE_KEY, owner)
	} catch {
		// vale finché non si chiude l'app
	}
}

// Il selettore: un tasto con l'icona di quello che è scelto, pieno quando
// l'elenco è ristretto. Toccato apre le tre voci, ognuna con quante sono.
// L'icona da sola va capita: quando si sceglie, e aprendo il quaderno
// quando non è su "Tutte", per un attimo un'etichetta dice cosa vuol dire.
function OwnerSelector({ owner, counts, onChange }) {
	const [isOpen, setOpen] = useState(false)
	const [hint, setHint] = useState(owner !== "all")
	const current = NOTEBOOK_OWNERS.find(option => option.id === owner) ?? NOTEBOOK_OWNERS[0]
	const isNarrowed = owner !== "all"

	useEffect(() => {
		if (!hint) return
		const done = setTimeout(() => setHint(false), 1800)
		return () => clearTimeout(done)
	}, [hint, owner])

	const choose = next => {
		setOpen(false)
		onChange(next)
		setHint(true)
	}

	return (
		<div style={{ position: "relative", flex: "none" }}>
			{isOpen && (
				// toccando fuori si chiude
				<div
					aria-hidden="true"
					onClick={() => setOpen(false)}
					style={{ position: "fixed", inset: 0, zIndex: 1 }}
				/>
			)}
			<AnimatePresence>
				{isOpen && (
					<motion.div
						key="menu"
						role="menu"
						aria-label="Di chi"
						initial={{ opacity: 0, y: 8, scale: 0.96 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: 8, scale: 0.96 }}
						transition={{ duration: 0.16 }}
						style={{
							position: "absolute",
							left: "-0.25rem",
							bottom: "calc(100% + 1rem)",
							zIndex: 2,
							width: "14.5rem",
							padding: "0.375rem",
							display: "flex",
							flexDirection: "column",
							gap: "2px",
							borderRadius: "20px",
							background: theme.colors.surface,
							boxShadow: theme.shadows.lift,
							transformOrigin: "bottom left",
						}}>
						{NOTEBOOK_OWNERS.map(option => {
							const isSelected = option.id === owner
							return (
								<button
									key={option.id}
									type="button"
									role="menuitemradio"
									aria-checked={isSelected}
									onClick={() => choose(option.id)}
									style={{
										display: "flex",
										alignItems: "center",
										gap: "0.75rem",
										height: "3rem",
										padding: "0 0.75rem",
										border: 0,
										borderRadius: "14px",
										background: isSelected ? theme.colors.background : "transparent",
										color: theme.colors.gunmetal,
										fontFamily: "inherit",
										fontSize: "0.9375rem",
										fontWeight: isSelected ? 600 : 500,
										textAlign: "left",
										cursor: "pointer",
									}}>
									<span style={{ width: "1.25rem", display: "inline-flex", justifyContent: "center" }}>
										<FontAwesomeIcon icon={OWNER_ICONS[option.id]} />
									</span>
									{option.label}
									<span
										style={{
											marginLeft: "auto",
											fontSize: "0.75rem",
											fontWeight: 500,
											color: theme.colors.text_light,
										}}>
										{counts[option.id]}
									</span>
								</button>
							)
						})}
					</motion.div>
				)}
				{hint && !isOpen && (
					<motion.span
						key="hint"
						role="status"
						initial={{ opacity: 0, y: 6 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 6 }}
						transition={{ duration: 0.2 }}
						style={{
							position: "absolute",
							left: 0,
							bottom: "calc(100% + 1rem)",
							display: "inline-flex",
							alignItems: "center",
							height: "2.125rem",
							padding: "0 0.875rem",
							borderRadius: "17px",
							background: theme.colors.gunmetal_fill,
							color: theme.colors.white,
							fontSize: "0.8125rem",
							fontWeight: 600,
							whiteSpace: "nowrap",
							boxShadow: theme.shadows.lift,
							pointerEvents: "none",
						}}>
						{current.short} · {counts[owner]}
					</motion.span>
				)}
			</AnimatePresence>
			<motion.div whileTap={{ scale: 0.9 }} style={{ position: "relative", zIndex: 2 }}>
				<Button
					w="3.5rem"
					h="3.5rem"
					bg={isNarrowed ? theme.colors.gunmetal_fill : theme.colors.surface}
					textColor={isNarrowed ? theme.colors.white : theme.colors.gunmetal}
					rounded={theme.rounded["20px"]}
					aria-label={`Di chi: ${current.label.toLowerCase()}`}
					aria-haspopup="menu"
					aria-expanded={isOpen}
					onClick={() => setOpen(open => !open)}>
					<FontAwesomeIcon icon={OWNER_ICONS[owner]} size="lg" />
				</Button>
			</motion.div>
		</div>
	)
}

// Il quaderno: le lezioni e gli esercizi da riusare, su un foglio con due
// linguette, con la ricerca e il + in basso come negli allievi. Toccare una
// voce la apre lì dove sta; dentro, **Modifica** la cambia o la toglie.
//
// Ci sono anche le voci che i colleghi condividono nel circolo: si
// cominciano a leggere qui, la prima volta che si apre il quaderno.
function NotebookContainer() {
	const Modal = useContext(modalContext)
	const { entries, isLoading, hasFailed, loadShared } = useContext(notebookContext)
	const [kind, setKind] = useState(NOTEBOOK_LESSON)
	const [filter, setFilter] = useState(NOTEBOOK_ALL)
	const [level, setLevel] = useState(null)
	const [chosenOwner, setChosenOwner] = useState(readOwner)
	const [search, setSearch] = useState("")
	// l'esercizio aperto a pagina intera, da dove si è aperto e con che
	// variante; se lo si toglie, si chiude
	const [open, setOpen] = useState(null)
	const [chosenView, setChosenView] = useState(readExercisesView)
	const openEntry = open ? (entries ?? []).find(entry => entry.entryID === open.entryID) : null

	useEffect(() => loadShared?.(), [loadShared])

	// il selettore c'è se c'è qualcosa dei colleghi: se no "Dei colleghi"
	// sarebbe vuoto, e "Mie" sarebbe tutto
	const hasColleagues = (entries ?? []).some(entry => entry.isMine === false)
	const owner = hasColleagues ? chosenOwner : "all"
	const changeOwner = next => {
		setChosenOwner(next)
		storeOwner(next)
	}
	const visible = filterByOwner(entries, owner)
	const ownerCounts = Object.fromEntries(
		NOTEBOOK_OWNERS.map(option => [
			option.id,
			filterByOwner(entries, option.id).filter(entry => entry.kind === kind).length,
		]),
	)

	const counts = Object.fromEntries(
		TABS.map(tab => [tab.kind, visible.filter(entry => entry.kind === tab.kind).length]),
	)
	// nella pillola in cima: "3 lezioni · 5 esercizi", gli stessi numeri
	// delle linguette
	usePhoneSummary(isLoading || hasFailed ? undefined : notebookSummary(counts))
	const filters = notebookFilters(visible, kind)
	const levels = notebookLevelFilters(visible, kind)
	// una pastiglia che non c'è più (se n'è andata l'ultima voce che la
	// teneva) non nasconde niente
	const activeFilter = filters.some(option => option.id === filter) ? filter : NOTEBOOK_ALL
	const activeLevel = levels.some(option => option.id === level) ? level : null
	// "Tutte" più una pastiglia sola restringerebbe a quasi tutto: servono almeno due scelte
	const showFilters = filters.length - 1 + levels.length >= 2
	const found = filterNotebook(searchNotebook(visible, kind, search), activeFilter, activeLevel)
	const openExercise = (entryID, from) =>
		setOpen({ entryID, from, variantID: found.find(entry => entry.entryID === entryID)?.variantID })
	const openNew = () => Modal.open("notebookEntry", { kind })
	const empty = EMPTY[kind]
	const isEmpty =
		!isLoading && !hasFailed && (entries ?? []).filter(entry => entry.kind === kind).length === 0
	const exercisesView =
		chosenView ??
		((entries ?? []).some(entry => entry.kind === NOTEBOOK_EXERCISE && entry.schema) ? "grid" : "list")

	// Passando fra griglia e lista le carte vanno dove vanno da sole
	// (`layoutId`); il foglio bianco che le tiene cambia altezza con loro,
	// negli stessi tempi, così non ne taglia nessuna per strada
	const sheet = useRef(null)
	const sheetHeight = useRef(null)
	const reduceMotion = useReducedMotion()

	const changeExercisesView = view => {
		if (view === exercisesView) return
		sheetHeight.current = sheet.current?.offsetHeight ?? null
		setChosenView(view)
		storeExercisesView(view)
	}

	useLayoutEffect(() => {
		const el = sheet.current
		const from = sheetHeight.current
		sheetHeight.current = null
		if (!el || !from || reduceMotion) return
		const to = el.offsetHeight
		if (!to || to === from) return
		el.style.height = `${from}px`
		const growing = animate(el, { height: [`${from}px`, `${to}px`] }, SWAP)
		const done = setTimeout(() => (el.style.height = ""), SWAP.duration * 1000 + 20)
		return () => {
			growing.stop()
			clearTimeout(done)
			el.style.height = ""
		}
	}, [exercisesView, reduceMotion])

	const changeKind = next => {
		setKind(next)
		setFilter(NOTEBOOK_ALL)
		setLevel(null)
	}

	// chi ha chiesto al telefono meno movimento, le carte le vede cambiare
	// di colpo
	return (
		<MotionConfig reducedMotion="user">
			<Container>
				{isSmartphone ? (
					<Div m={{ b: "0.75rem" }}>
						<PhonePageTitle>Quaderno</PhonePageTitle>
					</Div>
				) : (
					<Text
						textSize="display1"
						textWeight="bold"
						textColor={theme.colors.gunmetal}
						m={{ t: "1rem", b: "0.75rem" }}>
						Quaderno
					</Text>
				)}

				<Div d="flex" align="flex-end" justify="space-between" style={{ gap: "0.5rem" }}>
					<NotebookTabs kind={kind} counts={counts} onChange={changeKind} />
					{kind === NOTEBOOK_EXERCISE && !isLoading && !hasFailed && !isEmpty && (
						<ExercisesViewToggle view={exercisesView} onChange={changeExercisesView} />
					)}
				</Div>

				<Div
					bg={theme.colors.surface}
					shadow={theme.shadows.accent}
					style={{
						// le linguette partono dal bordo di sinistra: lì sopra ce n'è
						// sempre una, la scelta o l'altra, e l'angolo resta vivo. Tondo,
						// sotto "Lezioni" si vedeva il buco.
						borderRadius: "0 20px 20px 20px",
						overflow: "hidden",
						paddingBottom: isEmpty ? 0 : "0.5rem",
					}}>
					<motion.div
						ref={sheet}
						key={kind}
						animate={{ y: [12, 0] }}
						transition={{ ease: [0.04, 0.62, 0.23, 0.98], duration: 0.4 }}>
						{isLoading ? (
							<Text textSize="subheader" textAlign="center" p="1rem">
								Sto aprendo il quaderno...
							</Text>
						) : hasFailed ? (
							<Text textAlign="center" textColor={theme.colors.text_light} p="1rem">
								Non riesco ad aprire il quaderno. Riprova tra un po', e se continua
								segnalalo dal profilo.
							</Text>
						) : isEmpty ? (
							<EmptyState
								icon={empty.icon}
								title={empty.title}
								text={empty.text}
								actionText={empty.actionText}
								action={openNew}
							/>
						) : (
							<>
								{showFilters && (
									<NotebookFilters
										filters={filters}
										selected={activeFilter}
										onChange={setFilter}
										levels={levels}
										level={activeLevel}
										onLevel={setLevel}
									/>
								)}
								{found.length > 0 && kind === NOTEBOOK_EXERCISE && exercisesView === "grid" ? (
									// gli esercizi in galleria, con lo schema per copertina
									<NotebookGallery entries={found} divided={showFilters} onOpen={openExercise} />
								) : found.length > 0 && kind === NOTEBOOK_EXERCISE ? (
									// o a lista, per fase
									<NotebookPhaseList entries={found} divided={showFilters} onOpen={openExercise} />
								) : found.length > 0 ? (
									found.map((entry, i) => (
										<NotebookRow
											key={entry.entryID}
											entry={entry}
											highlightLevel={activeLevel}
											// il filo separa dalle pastiglie e dalla voce di sopra:
											// la prima, se le pastiglie non ci sono, non ce l'ha
											divided={showFilters || i > 0}
											onEdit={variantID =>
												Modal.open("notebookEntry", { entryID: entry.entryID, variantID })
											}
											onAddVariant={variantID =>
												Modal.open("notebookEntry", {
													entryID: entry.entryID,
													variantID,
													asNewVariant: true,
												})
											}
										/>
									))
								) : (
									<Text
										textSize="subheader"
										textAlign="center"
										p="1rem"
										style={
											showFilters
												? { boxShadow: `inset 0 1px 0 ${theme.colors.gunmetal_lighter}` }
												: undefined
										}>
										{search.trim() !== ""
											? "Niente nel quaderno con questa ricerca 🧐"
											: counts[kind] === 0
												? owner === "mine"
													? "Qui ci sono solo quelle dei colleghi"
													: "I colleghi non ne condividono ancora"
												: "Niente nel quaderno con questa pastiglia"}
									</Text>
								)}
							</>
						)}
					</motion.div>
				</Div>
			</Container>

			{openEntry && (
				<NotebookExerciseView
					entry={openEntry}
					variantID={open.variantID}
					from={open.from}
					onClose={() => setOpen(null)}
				/>
			)}

			<Div d="flex" justify="center">
				<motion.div
					style={
						isSmartphone
							? {
									position: "fixed",
									bottom: ABOVE_PHONE_NAV,
									transform: "translateX(-50%)",
									width: "calc(100% - 2rem)",
									zIndex: "1",
								}
							: {
									position: "fixed",
									width: "25rem",
									bottom: "5rem",
									zIndex: "1",
								}
					}
					initial={{ y: 20 }}
					animate={{ y: 0 }}
					exit={{ y: 20 }}>
					<Div
						bg={theme.colors.primary_light}
						rounded="20px"
						p="0.5rem"
						d="flex"
						align="center"
						style={{ gap: "0.5rem" }}>
						{hasColleagues && (
							<OwnerSelector owner={owner} counts={ownerCounts} onChange={changeOwner} />
						)}
						<Div flexGrow="1" style={{ minWidth: 0 }}>
							<TextInput
								value={search}
								handleChange={e => {
									setSearch(e.target.value)
									// i risultati partono dall'inizio dell'elenco: lasciati dove si
									// era scorso, con la tastiera aperta finivano fuori dallo schermo
									window.scrollTo({ top: 0 })
								}}
								placeholder="Cerca nel quaderno"
								aria-label="Cerca nel quaderno"
								m="0"
							/>
						</Div>
						<Div flexShrink="0">
							<motion.div whileTap={{ scale: 0.9 }}>
								<Button
									w="3.5rem"
									h="3.5rem"
									bg={theme.colors.primary}
									shadow={theme.shadows.accent}
									rounded={theme.rounded["20px"]}
									aria-label={
										kind === NOTEBOOK_EXERCISE
											? "Nuovo esercizio nel quaderno"
											: "Nuova lezione nel quaderno"
									}
									onClick={openNew}>
									<FontAwesomeIcon icon={faPlus} size="xl" />
								</Button>
							</motion.div>
						</Div>
					</Div>
				</motion.div>
			</Div>
		</MotionConfig>
	)
}

export default NotebookContainer
